import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PAGE_SIZES } from './utils/constants';
import { tableToTsv, tableToCsv, downloadFile } from './utils/formatters';
import { copyText } from '../../utils/clipboard';

/**
 * Toolbar above the table:
 * - Custom action buttons
 * - Pagination (page size selector + prev/next)
 * - Search input
 * - Copy / CSV export buttons
 * - Fullscreen toggle
 * - Selected count + bulk actions
 */
function TableToolbar({
    // Pagination
    page,
    totalPages,
    pageSize,
    pageSizes,
    showPagination = true,
    onPageChange,
    onPageSizeChange,
    // Search
    search,
    onSearchChange,
    // Custom actions
    toolbarActions,
    // Export
    exportFilename,
    exportFormat = 'csv',
    showExportCsv,
    showCopyTable,
    getExportData,
    // Selection
    selectable,
    selectedCount,
    bulkActions,
    // Fullscreen
    fullscreenEnabled,
    isFullscreen,
    onToggleFullscreen,
    // Loading
    loading,
    // Column visibility ("Columns" manage menu)
    manageableColumns,
    hiddenColumns,
    onToggleColumn,
    onShowAllColumns,
}) {
    const effectivePageSizes = pageSizes || PAGE_SIZES;
    const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
    const columnsMenuRef = useRef(null);

    useEffect(() => {
        if (!columnsMenuOpen) return;
        const handleClickOutside = (e) => {
            if (columnsMenuRef.current && !columnsMenuRef.current.contains(e.target)) {
                setColumnsMenuOpen(false);
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') setColumnsMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [columnsMenuOpen]);

    const hiddenSet = new Set(hiddenColumns || []);
    const showColumnsMenu = manageableColumns && manageableColumns.length > 0 && onToggleColumn;

    const handleCopyTable = useCallback(async () => {
        const { headers, rows } = getExportData();
        const tsv = tableToTsv(headers, rows);
        await copyText(tsv);
    }, [getExportData]);

    const handleExportCsv = useCallback(async () => {
        if (exportFormat === 'xlsx') {
            const XLSX = await import('xlsx');
            const { headers, rows } = getExportData(true);
            const book = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(book, XLSX.utils.aoa_to_sheet([headers, ...rows]), 'Данные');
            XLSX.writeFile(book, (exportFilename || 'export') + '.xlsx');
            return;
        }
        const { headers, rows } = getExportData();
        const csv = tableToCsv(headers, rows);
        downloadFile((exportFilename || 'export') + '.csv', csv);
    }, [getExportData, exportFilename, exportFormat]);

    return (
        <div className="ut-toolbar">
            {/* Left: custom actions */}
            {toolbarActions && toolbarActions.length > 0 && (
                <div className="ut-toolbar__group">
                    {toolbarActions.map((action, i) => (
                        <button
                            key={i}
                            type="button"
                            className={`ut-toolbar__btn ${action.primary ? 'is-primary' : 'ghost'}`}
                            onClick={action.onClick}
                            disabled={action.disabled || loading}
                            title={action.title || ''}
                        >
                            {action.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Center: pagination + search */}
            <div className="ut-toolbar__group">
                {showPagination && (
                    <div className="ut-pagination">
                        <label className="ut-pagination__label">
                            Строк:
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    onPageSizeChange(val === 'all' ? 'all' : Number(val));
                                }}
                                className="ut-pagination__select"
                            >
                                {effectivePageSizes.map((s) => (
                                    <option key={s} value={s}>
                                        {s === 'all' ? 'Все' : s}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button
                            type="button"
                            className="ut-pagination__btn"
                            disabled={page <= 1}
                            onClick={() => onPageChange(page - 1)}
                        >
                            ‹ Назад
                        </button>
                        <span className="ut-pagination__info">
                            {page} / {totalPages}
                        </span>
                        <button
                            type="button"
                            className="ut-pagination__btn"
                            disabled={page >= totalPages}
                            onClick={() => onPageChange(page + 1)}
                        >
                            Вперед ›
                        </button>
                    </div>
                )}

                {onSearchChange && (
                    <input
                        type="text"
                        className="ut-search"
                        placeholder="Поиск..."
                        value={search || ''}
                        onChange={(e) => onSearchChange(e.target.value)}
                    />
                )}
            </div>

            {/* Right: export, copy, fullscreen */}
            <div className="ut-toolbar__group">
                {selectedCount > 0 && bulkActions && (
                    <span className="ut-toolbar__selected">
                        Выбрано: {selectedCount}
                        {bulkActions.map((ba, i) => (
                            <button
                                key={i}
                                type="button"
                                className="ut-toolbar__btn ghost"
                                onClick={ba.onClick}
                            >
                                {ba.label}
                            </button>
                        ))}
                    </span>
                )}

                {showCopyTable && (
                    <button
                        type="button"
                        className="ut-toolbar__btn ghost"
                        onClick={handleCopyTable}
                        disabled={loading}
                    >
                        Скопировать
                    </button>
                )}

                {showExportCsv && (
                    <button
                        type="button"
                        className="ut-toolbar__btn ghost"
                        onClick={handleExportCsv}
                        disabled={loading}
                    >
                        {exportFormat === 'xlsx' ? 'Скачать Excel' : 'Скачать CSV'}
                    </button>
                )}

                {showColumnsMenu && (
                    <div className="ut-columns-menu" ref={columnsMenuRef}>
                        <button
                            type="button"
                            className="ut-toolbar__btn ghost"
                            onClick={() => setColumnsMenuOpen((v) => !v)}
                            title="Управление столбцами"
                            aria-haspopup="true"
                            aria-expanded={columnsMenuOpen}
                        >
                            Столбцы{hiddenSet.size > 0 ? ` (${hiddenSet.size} скрыто)` : ''}
                        </button>

                        {columnsMenuOpen && (
                            <div className="ut-columns-menu__popover">
                                <div className="ut-columns-menu__header">
                                    <span>Показать столбцы</span>
                                    <button
                                        type="button"
                                        className="ut-columns-menu__show-all"
                                        onClick={onShowAllColumns}
                                        disabled={hiddenSet.size === 0}
                                    >
                                        Показать все
                                    </button>
                                </div>
                                <div className="ut-columns-menu__list">
                                    {manageableColumns.map((col) => {
                                        const isHidden = hiddenSet.has(col.key);
                                        const locked = col.hideable === false;
                                        return (
                                            <label
                                                key={col.key}
                                                className={`ut-columns-menu__item${locked ? ' ut-columns-menu__item--locked' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!isHidden}
                                                    disabled={locked}
                                                    onChange={() => onToggleColumn(col.key)}
                                                />
                                                <span>{col.header}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {fullscreenEnabled && (
                    <button
                        type="button"
                        className="ut-toolbar__btn ut-toolbar__btn--icon"
                        onClick={onToggleFullscreen}
                        title={isFullscreen ? 'Свернуть' : 'На весь экран'}
                    >
                        {isFullscreen ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M5.5 0a.5.5 0 0 1 .5.5v4A1.5 1.5 0 0 1 4.5 6h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5zm5 0a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 10 4.5v-4a.5.5 0 0 1 .5-.5zM0 10.5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 6 11.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zm10 0a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4z" />
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z" />
                            </svg>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
}

export default React.memo(TableToolbar);
